'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, Checkbox } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import Link from 'next/link';

const cardBase =
  'w-full rounded-xl border px-4 py-4 transition-all duration-150 select-none';
const cardRow = 'flex items-start gap-3';
const dotBase =
  'mt-[2px] h-5 w-5 rounded-full border flex items-center justify-center transition-all';
const dotInner = 'h-[10px] w-[10px] rounded-full';

function cn(...cls: Array<string | false | undefined | null>) {
  return cls.filter(Boolean).join(' ');
}

function OptionCard({
  checked,
  title,
  description,
  right,
}: {
  checked: boolean;
  title: string;
  description?: string;
  right?: React.ReactNode;
}) {
  const border = checked
    ? 'border-[var(--color-accent,#22a447)]'
    : 'border-[#E5E7EB]';
  const bg = checked ? 'bg-[#ECFDF5]' : 'bg-white';

  const dotBorder = checked
    ? 'border-[var(--color-accent,#22a447)]'
    : 'border-[#9CA3AF]';
  const dotFill = checked
    ? 'bg-[var(--color-accent,#22a447)]'
    : 'bg-transparent';

  return (
    <div
      className={cn(
        cardBase,
        border,
        bg,
        'cursor-pointer',
        'hover:border-[var(--color-accent,#22a447)]/70 hover:bg-[#F9FAFB]'
      )}
    >
      <div className={cn(cardRow, 'justify-between')}>
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn(dotBase, dotBorder)}>
            <div className={cn(dotInner, dotFill)} />
          </div>

          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-[#111827]">
              {title}
            </div>
            {description ? (
              <div className="mt-1 text-[13px] leading-relaxed text-[#4B5563]">
                {description}
              </div>
            ) : null}
          </div>
        </div>

        {right ? <div className="ml-3 flex-shrink-0">{right}</div> : null}
      </div>
    </div>
  );
}

const Step1Content: React.FC = () => {
  const form = Form.useFormInstance();
  const accountType = Form.useWatch('account_type', form);
  const individualSubtype = Form.useWatch('individual_subtype', form);

  // keep your options exactly
  const individualOptions = useMemo(
    () => [
      { value: 'citizen', label: 'Citizen →' },
      { value: 'resident', label: 'Foreign national (Resident)' },
      { value: 'other', label: 'Other' },
    ],
    []
  );

  // Ensure NOT preselected: if field is empty, set undefined (not "individual")
  useEffect(() => {
    const cur = form?.getFieldValue?.('account_type');
    if (cur === undefined) {
      // do nothing (keeps it unselected)
      return;
    }
  }, [form]);

  const handleCardClick = useCallback(
    (type: 'individual' | 'organization') => {
      form.setFieldsValue({ account_type: type });

      if (type !== 'individual') {
        form.setFieldsValue({ individual_subtype: undefined });
      }

      form.validateFields(['account_type']);
    },
    [form]
  );

  const handleSubtypeClick = useCallback(
    (value: string) => {
      form.setFieldsValue({ individual_subtype: value });
      form.validateFields(['individual_subtype']);
    },
    [form]
  );

  const showSubtypeCards = accountType === 'individual';

  return (
    <div className="space-y-5">
      {/* Account type */}
      <Form.Item
        name="account_type"
        validateTrigger="onChange"
        rules={[
          { required: true, message: 'Please select an account type' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve(); // required rule handles it
              if (value === 'individual') return Promise.resolve();
              return Promise.reject(
                new Error('Selected option(s) currently not supported.')
              );
            },
          },
        ]}
        className="mb-0"
      >
        <div className="flex flex-col gap-4">
          {/* Individual card */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleCardClick('individual')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                handleCardClick('individual');
            }}
            className="outline-none"
          >
            <OptionCard
              checked={accountType === 'individual'}
              title="Individual"
              description="Create a personal account to access county services"
              right={
                accountType === 'individual' ? (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-accent,#22a447)] text-white">
                    <CheckOutlined style={{ fontSize: 12 }} />
                  </span>
                ) : null
              }
            />
          </div>

          {/* If you later enable Organization, add it here with disabled styling */}
        </div>
      </Form.Item>
      {/* Individual subtypes — separate cards, only visible AFTER individual is selected */}
      {showSubtypeCards && (
        <div className="pt-1">
          <div className="flex gap-4">
            {/* green line like your sign-in style */}
            <div className="w-[3px] rounded-full bg-[var(--color-accent,#22a447)]" />

            <div className="flex-1 space-y-3">
              <div className="text-[14px] font-semibold text-[#111827]">
                Select individual type
              </div>

              <Form.Item
                name="individual_subtype"
                className="mb-0"
                validateTrigger="onChange"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value)
                        return Promise.reject(
                          new Error('Please select an individual type')
                        );
                      if (value === 'citizen') return Promise.resolve();
                      return Promise.reject(
                        new Error('Selected option(s) currently not supported.')
                      );
                    },
                  },
                ]}
              >
                <div className="flex flex-col gap-3">
                  {individualOptions.map((opt) => {
                    const checked = individualSubtype === opt.value;
                    return (
                      <div
                        key={opt.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSubtypeClick(opt.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ')
                            handleSubtypeClick(opt.value);
                        }}
                        className="outline-none"
                      >
                        <OptionCard checked={checked} title={opt.label} />
                      </div>
                    );
                  })}
                </div>
              </Form.Item>
            </div>
          </div>
        </div>
      )}
      {/* Terms — more professional */}
      <Form.Item
        name="agree"
        valuePropName="checked"
        className="!mb-0"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(
                    new Error(
                      'Please tick the box to confirm you’ve read the Terms and Privacy Policy.'
                    )
                  ),
          },
        ]}
      >
        <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3">
          <Checkbox>
            <span className="text-sm text-[#374151]">
              I agree to the{' '}
              <Link
                href="/terms"
                className="font-semibold hover:underline"
                style={{ color: 'var(--color-primary, #22a447)' }}
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link
                href="/privacy"
                className="font-semibold hover:underline"
                style={{ color: 'var(--color-primary, #22a447)' }}
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </Link>
              .
            </span>
          </Checkbox>
        </div>
      </Form.Item>
    </div>
  );
};

export default Step1Content;
